import { useState } from 'react';
import type {
  BoardroomPageOne,
  BoardroomBrand,
  BoardroomBrandFinding,
  BoardroomBrandLevel,
  BoardroomBrandAimedPage,
  BoardroomBrandHolder,
  BoardroomBrandRow,
  BoardroomBrandSummary,
} from '../../../types';
import SerpModal from '../../SerpModal';
import ChatMarkdown from '../../common/ChatMarkdown';
import { useEscapeKey } from '../../../hooks/useEscapeKey';
import DataTable from './DataTable';
import { EngineTag, EnginesNote, engineCounts, engineLabel } from './engines';
import type { Column } from './tableColumns';
import { CountButton, SerpLink, TermList, useOpened, type SerpTarget } from './SearchMarketReport';

/**
 * Brand interception: the searches people make about one company, and who holds
 * page one for them — the company's own pages, rivals' pages (and whether they
 * are aimed at its name), rivals' ads on its name, and third-party sites.
 *
 * It says only what the evidence reaches: a rival on page one for
 * "<name> alternatives" is targeting and visibility, not lost customers. Clicks
 * are estimates from position.
 */

const n = (v: number | null | undefined) => (v === null || v === undefined ? '-' : v.toLocaleString());

/** GOOGLE'S 0 IS NOT ZERO. Its keyword tool reports a search too rare to count
 *  as 0, which can still be up to about ten a month. A search with no number
 *  looked up at all is "—". */
export const RARE_TITLE = 'Google reports searches this rare as 0; it can still be up to about 10 a month';
export function volumeLabel(v: number | null | undefined): string {
  if (v === null || v === undefined) return '-';
  return v === 0 ? 'under 10' : v.toLocaleString();
}
function Volume({ v }: { v: number | null | undefined }) {
  return <span title={v === 0 ? RARE_TITLE : undefined}>{volumeLabel(v)}</span>;
}
const section = 'border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 p-3';
const h3 = 'text-[11px] uppercase tracking-wide text-gray-400 mb-1';
const LINK = 'text-indigo-600 dark:text-indigo-400 hover:underline';

export const CLASS_LABEL: Record<string, string> = {
  branded: 'the name',
  commercial: 'pricing, reviews, demo',
  problems: 'problems',
  alternatives: 'alternatives',
  comparison: 'vs a rival',
  people: 'founders',
};

const STATUS_WORDS: Record<BoardroomBrandRow['status'], string> = {
  searched: '',
  pending: 'searching…',
  failed: 'search failed',
  waiting: 'not searched',
};

/** What each evidence level means, in the reader's words. */
export const LEVEL_WORDS: Record<BoardroomBrandLevel, { label: string; hint: string; tone: string }> = {
  displacement: {
    label: 'Moved ahead',
    hint: 'Since an earlier look, a rival has moved ahead of the company on the same searches. Needs two looks.',
    tone: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',
  },
  interception: {
    label: 'Interception',
    hint: 'Holds a top-3 place or a real share of clicks on the searches people make when choosing - or advertises on the company’s searches.',
    tone: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  },
  emerging: {
    label: 'Emerging',
    hint: 'Has a page aimed at the company on page one, below the top 3.',
    tone: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
  },
  open: {
    label: 'Open lane',
    hint: 'Choosing searches where no rival is on page one and the company is not top 3.',
    tone: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  },
};

/** WATCHING THE NAME. One look says who is there; a weekly re-check says who
 *  is arriving, which is what a rival aiming at the name looks like. */
function WatchBar({ watch, onWatch, busy }: { watch?: BoardroomBrand['watch']; onWatch?: () => void; busy: boolean }) {
  if (watch?.watching) {
    return (
      <p className="mt-2 flex flex-wrap items-center gap-2 text-xs text-emerald-800 dark:text-emerald-200" data-testid="brand-watching">
        <span>
          Watching {watch.searches} of these searches every {watch.every_days === 7 ? 'week' : `${watch.every_days} days`}: you get an
          alert when a rival turns up on them or moves.
        </span>
        {watch.subject_id && (
          <a href={`/watch/subjects/${watch.subject_id}`} className={LINK}>
            Open the watch →
          </a>
        )}
      </p>
    );
  }
  if (!onWatch) return null;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
      <button
        type="button"
        onClick={onWatch}
        disabled={busy}
        className="rounded border border-indigo-300 px-2 py-0.5 text-indigo-700 hover:bg-indigo-50 disabled:opacity-50 dark:border-indigo-700 dark:text-indigo-300 dark:hover:bg-indigo-900/30"
        data-testid="brand-watch"
      >
        {busy ? 'Starting the watch…' : 'Watch these searches'}
      </button>
      <span className="text-gray-500 dark:text-gray-400">
        {watch?.subject_id && !watch.watching
          ? 'The watch is paused. Pressing this starts it again.'
          : 'Re-searches up to 25 of them once a week (one search each), and alerts you when a rival turns up or moves.'}
      </span>
    </div>
  );
}

const WIN_PAGE = 15;

const LABEL_TONE: Record<string, string> = {
  weak: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  mixed: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  strong: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
};
const LABEL_WORDS: Record<string, string> = {
  weak: 'easy to win',
  mixed: 'some room',
  strong: 'hard to win',
};
const KIND_WORDS: Record<string, string> = {
  company: 'on the board',
  authority: 'government / university / encyclopedia',
  forum: 'forum',
  social: 'social',
  'review site': 'review site',
  marketplace: 'marketplace',
  video: 'video',
  publisher: 'site',
};

/** ✓ uses the keyword, ✗ does not, – not read: one mark per place a page says what it is for. */
function Mark({ v }: { v: boolean | null | undefined }) {
  return v === true ? (
    <span className="text-rose-600 dark:text-rose-300" title="Uses the keyword here">✓</span>
  ) : v === false ? (
    <span className="text-emerald-600 dark:text-emerald-300" title="Does not use the keyword here">✗</span>
  ) : (
    <span className="text-gray-300 dark:text-gray-600" title="Not read: the page has not been downloaded">–</span>
  );
}

const SPOT_HEAD: Record<string, string> = {
  title: 'Title',
  url: 'Address',
  h1: 'Heading',
  opening: 'First para',
  snippet: 'Snippet',
};

const WHY_WORDS: Record<string, string> = {
  'big site': 'big site',
  'home page': 'home page',
  forum: 'forum thread',
  'not checked': 'nothing read',
  'uses it': 'written for it',
  'does not use it': 'not written for it',
};

function PageOneDetail({ p, onOpen }: { p: BoardroomPageOne; onOpen: (id: number) => void }) {
  const spots = p.spots ?? ['title', 'url'];
  return (
    <div className="my-1 ml-2 rounded border border-gray-100 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-900/40" data-testid="brand-win-detail">
      <ul className="mb-1 list-disc pl-4 text-xs text-gray-700 dark:text-gray-200">
        {p.reasons.map(r => (
          <li key={r}>{r}</li>
        ))}
      </ul>
      <p className="mb-1 text-[11px] text-gray-500 dark:text-gray-400">
        ✓ uses the keyword there · ✗ does not · – not read yet. An open result is one not written for the keyword: room for
        a page that is. What counts is set in Settings → Keywords.
      </p>
      <table className="w-full text-xs">
        <thead className="text-[11px] uppercase tracking-wide text-gray-400">
          <tr>
            <th className="pr-2 text-left font-normal">#</th>
            <th className="pr-2 text-left font-normal">Result</th>
            {spots.map(s => (
              <th key={s} className="pr-2 font-normal">
                {SPOT_HEAD[s] ?? s}
              </th>
            ))}
            <th className="pr-2 font-normal">Open</th>
            <th className="text-left font-normal">Site</th>
          </tr>
        </thead>
        <tbody>
          {p.results.map(r => (
            <tr key={`${r.position}-${r.url}`} className="border-t border-gray-100 dark:border-gray-700/60">
              <td className="pr-2 tabular-nums text-gray-400">{r.position}</td>
              <td className="pr-2 max-w-[22rem] truncate">
                {r.url ? (
                  <a href={r.url} target="_blank" rel="noopener noreferrer" className="hover:underline" title={r.url}>
                    {r.title || r.url}
                  </a>
                ) : (
                  r.title
                )}
              </td>
              {spots.map(s => (
                <td key={s} className="pr-2 text-center">
                  <Mark v={r.marks?.[s]} />
                </td>
              ))}
              <td className="pr-2 text-center" title={WHY_WORDS[r.why] ?? r.why}>
                {r.is_open ? (
                  <span className="text-emerald-600 dark:text-emerald-300">open</span>
                ) : (
                  <span className="text-gray-400">no</span>
                )}
              </td>
              <td className="text-gray-500 dark:text-gray-400">
                {r.company_id ? (
                  <button type="button" onClick={() => onOpen(r.company_id as number)} className={LINK}>
                    {r.company || r.site}
                  </button>
                ) : (
                  r.site
                )}{' '}
                · {KIND_WORDS[r.kind] ?? r.kind}
                {r.home_page && ' · home page'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** WHERE THE BIGGER RISK IS. A small company's own searches are a few a month;
 *  the market's are thousands, and they are won by whoever is on page one. */
function WinSection({
  win,
  name,
  side,
  onOpen,
  onSerp,
}: {
  win: NonNullable<BoardroomBrand['win']>;
  name: string;
  side?: 'ours' | 'theirs';
  onOpen: (id: number) => void;
  onSerp: (t: SerpTarget) => void;
}) {
  const [shown, setShown] = useState(WIN_PAGE);
  const [openRow, setOpenRow] = useState<string | null>(null);
  const rows = win.rows.slice(0, shown);
  const left = win.rows.length - rows.length;
  return (
    <section className={section} data-testid="brand-win">
      <h3 className={h3}>{side === 'ours' ? `Where ${name} could win` : `Category searches ${name} is missing`}</h3>
      <p className="mb-1 text-xs text-gray-600 dark:text-gray-300">
        Searches about the category, not about {name}, where {name} is not in the top 3 - biggest first. Further down are
        smaller, more specific searches, often closer to someone looking for a product like this one.
      </p>
      <EnginesNote counts={engineCounts(win.rows)} className="mb-1" />
      <details className="mb-1 text-xs text-gray-600 dark:text-gray-300" data-testid="brand-win-how">
        <summary className="cursor-pointer text-gray-500 dark:text-gray-400">How the opening score works</summary>
        <div className="mt-1 space-y-1 pl-2">
          <p>
            Out of 100: the share of the leading results that are <strong>open</strong> - not written for this keyword, so a
            page that is has room to take their place. Higher is more open. {`${name}'s`} own result is not counted.
          </p>
          <p>
            For each result we check the places a page says what it is about: its <strong>title</strong>,{' '}
            <strong>web address</strong>, <strong>main heading</strong> and <strong>first paragraph</strong> (the last two once
            the page has been downloaded). A place uses the keyword when it has every word of it, in any order; “records”
            counts as “record”. A result that uses it in few of them is open.
          </p>
          <p>
            Then the site: a <strong>forum thread</strong> ranking means nobody has written a proper page, so it is open. A{' '}
            <strong>home page</strong>, or a <strong>big site</strong> (government, encyclopedia, the largest publishers), ranks on
            who it is, so it is not.
          </p>
          <p>
            <span className={`rounded px-1 ${LABEL_TONE.weak}`}>easy to win</span>{' '}
            <span className={`rounded px-1 ${LABEL_TONE.mixed}`}>some room</span>{' '}
            <span className={`rounded px-1 ${LABEL_TONE.strong}`}>hard to win</span>. How many results are read, which places
            count, and where each label starts are your settings, in Settings → Keywords - the same rules as the ★ in your
            keyword lists. Ads are shown but not scored: they say the buyers are worth paying for, not that the page is
            easy. Not measured: links to each site, page speed, how recent the page is.
          </p>
        </div>
      </details>
      {win.rows.length === 0 ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {win.searched === 0
            ? 'None of the category searches has been run yet.'
            : `${name} is in the top 3 on every category search the room has run.`}
        </p>
      ) : (
        <table className="w-full text-xs">
          <thead className="text-[11px] uppercase tracking-wide text-gray-400">
            <tr>
              <th className="pr-3 text-left font-normal">Search</th>
              <th className="pr-3 text-right font-normal">Searches/mo</th>
              <th className="pr-3 text-right font-normal">{name}</th>
              <th className="pr-3 text-left font-normal">Opening</th>
              <th className="text-left font-normal">Who holds it</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const p = r.page_one;
              const open = openRow === r.q;
              return (
                <tr key={r.q} className="border-t border-gray-100 align-top dark:border-gray-700/60">
                  <td className="pr-3 py-0.5" colSpan={open ? 5 : 1}>
                    <SerpLink keyword={r.q} keywordId={r.keyword_id} onSerp={onSerp} />
                    <EngineTag engine={r.engine} />
                    {open && (
                      <>
                        <span className="ml-2 text-gray-400">
                          <Volume v={r.volume} /> searches/mo · {name} {r.position ? `#${r.position}` : 'not on page one'}
                        </span>
                        <button type="button" onClick={() => setOpenRow(null)} className={`ml-2 ${LINK}`}>
                          close
                        </button>
                        {p && <PageOneDetail p={p} onOpen={onOpen} />}
                      </>
                    )}
                  </td>
                  {!open && (
                    <>
                      <td className="pr-3 text-right tabular-nums">
                        <Volume v={r.volume} />
                      </td>
                      <td className="pr-3 text-right tabular-nums">{r.position ? `#${r.position}` : 'not on page one'}</td>
                      <td className="pr-3 whitespace-nowrap">
                        {p && p.score !== null && p.label ? (
                          <button
                            type="button"
                            onClick={() => setOpenRow(r.q)}
                            className="hover:underline"
                            title="Show why"
                            data-testid="brand-win-score"
                          >
                            <span className={`rounded px-1 font-medium ${LABEL_TONE[p.label]}`}>{p.score}</span>{' '}
                            {LABEL_WORDS[p.label]}
                            {p.ads > 0 && <span className="text-gray-400"> · {p.ads} ads</span>}{' '}
                            <span className="text-gray-400">▸</span>
                          </button>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td>
                        {r.holders.length === 0 ? (
                          <span className="text-gray-400">nobody on the board</span>
                        ) : (
                          r.holders.map((h, i) => (
                            <span key={h.id}>
                              {i > 0 && ', '}
                              <button type="button" onClick={() => onOpen(h.id)} className={LINK}>
                                {h.name}
                              </button>{' '}
                              <span className="text-gray-400">#{h.position}</span>
                            </span>
                          ))
                        )}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {left > 0 && (
        <button
          type="button"
          onClick={() => setShown(n => n + WIN_PAGE)}
          className={`mt-1 text-xs ${LINK}`}
          data-testid="brand-win-more"
        >
          Show {Math.min(WIN_PAGE, left)} more - smaller, more specific searches ({left} left)
        </button>
      )}
      <p className="mt-1 text-[11px] text-gray-400" data-testid="brand-win-coverage">
        From the {win.searched} category search{win.searched === 1 ? '' : 'es'} the room has run
        {win.more > 0 ? `; ${win.more} more not sent` : ''}.
        {win.waiting > 0 &&
          ` ${win.waiting} more are waiting to be searched, so this list is not the whole market yet: a dig with the Search market table switched on searches them, biggest first.`}
      </p>
    </section>
  );
}

function Finding({ f, onOpen, onSerp }: { f: BoardroomBrandFinding; onOpen: (id: number) => void; onSerp: (t: SerpTarget) => void }) {
  const [open, setOpen] = useState(false);
  const lw = LEVEL_WORDS[f.level];
  return (
    <li className="py-1.5 border-t border-gray-100 dark:border-gray-700/60 first:border-t-0" data-testid={`finding-${f.level}`}>
      <div className="flex flex-wrap items-start gap-2">
        <span className={`shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium ${lw.tone}`} title={lw.hint}>
          {lw.label}
        </span>
        <span
          className={`shrink-0 text-[11px] uppercase tracking-wide ${f.reading === 'threat' ? 'text-rose-600 dark:text-rose-300' : 'text-emerald-700 dark:text-emerald-300'}`}
        >
          {f.reading}
        </span>
        <span className="flex-1 min-w-0 text-gray-800 dark:text-gray-100">
          {f.who.kind === 'competitor' && f.who.id ? (
            <button type="button" onClick={() => onOpen(f.who.id as number)} className={`${LINK} mr-1`}>
              {f.who.name}
            </button>
          ) : null}
          {f.headline}
        </span>
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          aria-expanded={open}
          className={`shrink-0 ${LINK}`}
          data-testid={`finding-receipts-${f.level}`}
        >
          {open ? 'hide' : `${f.receipts.length} search${f.receipts.length === 1 ? '' : 'es'}`}
        </button>
      </div>
      {open && (
        <table className="mt-1 ml-2 text-xs" data-testid="finding-receipts">
          <thead className="text-[11px] uppercase tracking-wide text-gray-400">
            <tr>
              <th className="pr-3 text-left font-normal">Search</th>
              <th className="pr-3 text-left font-normal">Engine</th>
              <th className="pr-3 text-right font-normal">Searches/mo</th>
              <th className="pr-3 text-right font-normal">Theirs</th>
              <th className="pr-3 text-right font-normal">The company</th>
              <th className="text-left font-normal">Page</th>
            </tr>
          </thead>
          <tbody>
            {f.receipts.map(r => (
              <tr key={r.q}>
                <td className="pr-3">
                  <SerpLink keyword={r.q} keywordId={r.keyword_id} onSerp={onSerp} />
                </td>
                <td className="pr-3 text-gray-500 dark:text-gray-400" title={engineLabel(r.engine)}>
                  {r.engine ?? '-'}
                </td>
                <td className="pr-3 text-right tabular-nums">
                  <Volume v={r.volume} />
                </td>
                <td className="pr-3 text-right tabular-nums">{r.position ? `#${r.position}` : '-'}</td>
                <td className="pr-3 text-right tabular-nums">{r.target_position ? `#${r.target_position}` : 'off page one'}</td>
                <td className="truncate max-w-[18rem]">
                  {r.url ? (
                    <a href={r.url} target="_blank" rel="noopener noreferrer" className="hover:underline" title={r.url}>
                      {r.title || r.url}
                    </a>
                  ) : (
                    ''
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </li>
  );
}

/** The company's website on the board is missing or somebody else's: its own
 *  pages are then recognised by name, which can miss some. */
export function SiteWarning({
  text,
  onFix,
  evidence,
  onOpen,
}: {
  text: string;
  onFix?: () => void;
  /** Items to check the address against, each opened on click. */
  evidence?: { id: number; title: string; url: string | null; why: string }[];
  onOpen?: (id: number) => void;
}) {
  return (
    <div className="rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-2 py-1 text-xs text-amber-900 dark:text-amber-100" data-testid="brand-site-warning">
      <p>
        {text}
        {onFix && (
          <>
            {' '}
            <button type="button" onClick={onFix} className="underline">
              Open its page
            </button>
          </>
        )}
      </p>
      {/* THE EVIDENCE, AS THINGS TO OPEN. A warning that only says "check
          this" leaves the reader to go and find what to check it against. */}
      {(evidence ?? []).length > 0 && (
        <ul className="mt-1 flex flex-col gap-0.5" data-testid="brand-site-evidence">
          {evidence!.map(e => (
            <li key={e.id}>
              <span className="text-amber-700 dark:text-amber-300">{e.why}: </span>
              {onOpen ? (
                <button type="button" onClick={() => onOpen(e.id)} className="underline">
                  {e.title}
                </button>
              ) : (
                <span>{e.title}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Whose side the reader is on — the same rows read as threats or openings. */
function SideToggle({ name, side, onSide }: { name: string; side: 'ours' | 'theirs'; onSide: (s: 'ours' | 'theirs') => void }) {
  const b = (on: boolean) =>
    `px-2 py-0.5 text-xs ${on ? 'bg-indigo-600 text-white' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`;
  return (
    <div className="inline-flex overflow-hidden rounded border border-gray-300 dark:border-gray-600" role="group" data-testid="brand-side">
      <button type="button" className={b(side === 'ours')} aria-pressed={side === 'ours'} onClick={() => onSide('ours')}>
        We are {name}
      </button>
      <button type="button" className={b(side === 'theirs')} aria-pressed={side === 'theirs'} onClick={() => onSide('theirs')}>
        We compete with {name}
      </button>
    </div>
  );
}

/** The progress line, while searches are still landing. */
export function progressLine(p?: { queries: number; searched: number; pending: number; failed: number } | null): string {
  if (!p) return '';
  const parts = [`${p.searched} of ${p.queries} searched`];
  if (p.pending) parts.push(`${p.pending} searching`);
  if (p.failed) parts.push(`${p.failed} failed`);
  return parts.join(' · ');
}

function Tile({ label, value, hint, testId }: { label: string; value: string; hint: string; testId?: string }) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-2 py-1.5 min-w-[7rem]" title={hint} data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wide text-gray-400">{label}</div>
      <div className="text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-100">{value}</div>
    </div>
  );
}

/** The few numbers, for the company page and the top of the report. */
export function BrandTiles({ s }: { s: BoardroomBrandSummary }) {
  if (!s.started) return null;
  return (
    <div className="flex flex-wrap gap-2" data-testid="brand-tiles">
      <Tile label="Its own pages" value={`${s.target_share ?? 0}%`} hint="Share of estimated clicks on its searches that land on its own pages" testId="brand-own" />
      <Tile label="Rivals" value={`${s.interception ?? 0}%`} hint="Share of estimated clicks on its searches that land on rivals' pages" testId="brand-rivals" />
      <Tile label="Third parties" value={`${s.third_party_share ?? 0}%`} hint="Review sites, forums, marketplaces and publishers" />
      <Tile
        label="Owns its name"
        value={`${s.ownership ?? 0}%`}
        hint={`Its share of page one on searches for its name; #1 on ${s.number_one ?? 0} of ${s.branded_queries ?? 0}`}
      />
      <Tile label="Rivals' ads" value={`${s.paid_queries ?? 0}`} hint="Of its searches, how many show a rival's ad" testId="brand-ads" />
      <Tile label="Pages aimed at it" value={`${s.aimed_pages ?? 0}`} hint="Alternatives, comparison and review pages about it, by anyone but itself" />
    </div>
  );
}

export default function BrandReport({
  data,
  onOpen,
  onRestart,
  restarting = false,
  onSide,
  onWriteUp,
  writing = false,
  roomName,
  onWatch,
  watchBusy = false,
}: {
  data: BoardroomBrand;
  /** Re-check these searches every week, with alerts. */
  onWatch?: () => void;
  watchBusy?: boolean;
  /** Write it up for a marketing or executive team. */
  onWriteUp?: () => void;
  writing?: boolean;
  onOpen: (itemId: number) => void;
  /** Whose side the reader is on; remembered on the card. */
  onSide?: (side: 'ours' | 'theirs') => void;
  /** Search again (and start it, when it never has been). */
  onRestart?: () => void;
  restarting?: boolean;
  roomName?: string;
}) {
  const [serp, setSerp] = useState<SerpTarget | null>(null);
  const rivals = useOpened();
  const thirds = useOpened();
  useEscapeKey(!serp && Boolean(rivals.opened || thirds.opened), () => {
    rivals.close();
    thirds.close();
  });
  const name = data.target.title;

  const restart = onRestart && (
    <button
      type="button"
      onClick={onRestart}
      disabled={restarting}
      className="px-2 py-0.5 text-xs rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
      data-testid="brand-restart"
    >
      {restarting ? 'Starting…' : data.started ? 'Search again' : `Who's going after ${name}'s searches?`}
    </button>
  );

  if (!data.started || !data.metrics || !data.progress) {
    return (
      <div className="space-y-2 text-xs text-gray-500 dark:text-gray-400">
        <p>Nobody has looked at what people search about {name} yet.</p>
        {restart}
      </div>
    );
  }
  const m = data.metrics;

  const holderCount = (state: ReturnType<typeof useOpened>, keyOf: (h: BoardroomBrandHolder) => string): Column<BoardroomBrandHolder> => ({
    key: 'keywords',
    label: 'Searches',
    width: 6,
    align: 'right',
    value: h => h.keywords,
    cell: h => (
      <CountButton
        value={h.keywords}
        open={state.opened?.key === keyOf(h)}
        onClick={() => state.toggle(keyOf(h), 'all')}
        title={`The searches about ${name} it is on page one for`}
        testId={`brand-terms-${keyOf(h)}`}
      />
    ),
  });
  const holderDetail = (state: ReturnType<typeof useOpened>, keyOf: (h: BoardroomBrandHolder) => string, label: (h: BoardroomBrandHolder) => string) => ({
    expanded: (h: BoardroomBrandHolder) => state.opened?.key === keyOf(h),
    detail: (h: BoardroomBrandHolder) => (
      <TermList terms={h.terms} onSerp={setSerp} heading={`${label(h)} · page one for ${h.terms.length} of ${name}'s searches`} />
    ),
  });

  const rivalKey = (h: BoardroomBrandHolder) => `rival-${h.id}`;
  const rivalCols: Column<BoardroomBrandHolder>[] = [
    {
      key: 'name',
      label: 'Rival',
      width: 16,
      always: true,
      value: h => h.name ?? '',
      cell: h => (
        <button type="button" onClick={() => h.id && onOpen(h.id)} className={`${LINK} text-left`}>
          {h.name}
        </button>
      ),
    },
    { key: 'share', label: 'Share of clicks', width: 8, align: 'right', value: h => h.share, cell: h => `${h.share}%` },
    { key: 'clicks', label: 'Clicks/mo', width: 7, align: 'right', value: h => h.clicks, cell: h => n(h.clicks) },
    holderCount(rivals, rivalKey),
    { key: 'best', label: 'Best', width: 5, align: 'right', value: h => h.best },
    { key: 'ads', label: 'Ads on it', width: 6, align: 'right', value: h => h.ads ?? 0 },
    { key: 'aimed', label: 'Pages aimed at it', width: 8, align: 'right', value: h => h.aimed_pages ?? 0 },
  ];

  const thirdKey = (h: BoardroomBrandHolder) => `third-${h.domain}`;
  const thirdCols: Column<BoardroomBrandHolder>[] = [
    {
      key: 'domain',
      label: 'Site',
      width: 16,
      always: true,
      value: h => h.domain ?? '',
      cell: h => (
        <a href={`https://${h.domain}`} target="_blank" rel="noopener noreferrer" className="text-gray-900 dark:text-gray-100 hover:underline">
          {h.domain}
        </a>
      ),
    },
    { key: 'type', label: 'Kind', width: 8, value: h => h.site_type ?? '' },
    { key: 'share', label: 'Share of clicks', width: 8, align: 'right', value: h => h.share, cell: h => `${h.share}%` },
    { key: 'clicks', label: 'Clicks/mo', width: 7, align: 'right', value: h => h.clicks, cell: h => n(h.clicks) },
    holderCount(thirds, thirdKey),
    { key: 'best', label: 'Best', width: 5, align: 'right', value: h => h.best },
  ];

  const aimedCols: Column<BoardroomBrandAimedPage>[] = [
    {
      key: 'title',
      label: 'Page',
      width: 22,
      always: true,
      value: a => a.title,
      cell: a => (
        <a href={a.url} target="_blank" rel="noopener noreferrer" className={LINK} title={a.url}>
          {a.title || a.url}
        </a>
      ),
    },
    {
      key: 'owner',
      label: 'Whose',
      width: 12,
      value: a => a.owner_name,
      cell: a =>
        a.owner === 'competitor' && a.owner_id ? (
          <button type="button" onClick={() => onOpen(a.owner_id as number)} className={`${LINK} text-left`}>
            {a.owner_name}
          </button>
        ) : (
          a.owner_name
        ),
    },
    {
      key: 'deliberate',
      label: 'Kind',
      width: 10,
      value: a => (a.deliberate ? 'rival’s own page' : a.site_type ?? 'third party'),
      cell: a =>
        a.deliberate ? <span className="text-rose-700 dark:text-rose-300">rival’s own page</span> : a.site_type ?? 'third party',
    },
    {
      key: 'query',
      label: 'Found on',
      width: 14,
      value: a => a.query,
      cell: a => <SerpLink keyword={a.query} keywordId={a.keyword_id} onSerp={setSerp} />,
    },
    { key: 'position', label: 'Position', width: 6, align: 'right', value: a => a.position },
  ];

  const rowCols: Column<BoardroomBrandRow>[] = [
    {
      key: 'q',
      label: 'Search',
      width: 18,
      always: true,
      value: r => r.q,
      cell: r => <SerpLink keyword={r.q} keywordId={r.status === 'searched' ? r.keyword_id : null} onSerp={setSerp} />,
    },
    { key: 'qclass', label: 'About', width: 9, value: r => CLASS_LABEL[r.qclass] ?? r.qclass },
    {
      key: 'engine',
      label: 'Engine',
      width: 7,
      value: r => r.engine ?? null,
      cell: r => (r.engine ? <span title={engineLabel(r.engine)}>{r.engine}</span> : <span className="text-gray-400">-</span>),
    },
    { key: 'intent', label: 'Intent', width: 8, value: r => r.intent },
    { key: 'volume', label: 'Searches/mo', width: 7, align: 'right', value: r => r.volume, cell: r => <Volume v={r.volume} /> },
    {
      key: 'top_bid',
      label: 'Top bid',
      width: 6,
      align: 'right',
      value: r => r.top_bid,
      cell: r => (r.top_bid !== null && r.top_bid !== undefined ? `$${r.top_bid}` : '-'),
    },
    {
      key: 'target_position',
      label: `${data.target.title}`,
      width: 7,
      align: 'right',
      value: r => (r.status === 'searched' ? r.target_position ?? 99 : null),
      cell: r =>
        r.status !== 'searched' ? (
          <span className="italic text-gray-400">{STATUS_WORDS[r.status]}</span>
        ) : r.target_position ? (
          `#${r.target_position}`
        ) : (
          <span className="text-rose-700 dark:text-rose-300" title="Not on page one">
            off page one
          </span>
        ),
    },
    {
      key: 'best_rival',
      label: 'Best rival',
      width: 12,
      value: r => r.best_rival?.position ?? null,
      cell: r =>
        r.best_rival ? (
          <span>
            <button type="button" onClick={() => onOpen(r.best_rival!.id)} className={LINK}>
              {r.best_rival.name}
            </button>{' '}
            #{r.best_rival.position}
          </span>
        ) : (
          ''
        ),
    },
    {
      key: 'owners',
      label: 'Page one',
      width: 12,
      sortable: false,
      value: r => (r.status === 'searched' ? `${r.owners.target} own · ${r.owners.competitor} rivals · ${r.owners.third} others` : ''),
      cell: r =>
        r.status === 'searched' ? (
          <span className="text-gray-500 dark:text-gray-400 whitespace-nowrap">
            {r.owners.target} own · {r.owners.competitor} rival{r.owners.competitor === 1 ? '' : 's'} · {r.owners.third} other
          </span>
        ) : (
          ''
        ),
    },
    {
      key: 'ads',
      label: 'Rival ads',
      width: 10,
      value: r => r.ads.filter(a => a.owner === 'competitor').map(a => a.owner_name).join(', '),
      cell: r => {
        const theirs = r.ads.filter(a => a.owner === 'competitor');
        return theirs.length ? (
          <span className="text-rose-700 dark:text-rose-300">{theirs.map(a => a.owner_name).join(', ')}</span>
        ) : (
          ''
        );
      },
    },
  ];

  return (
    <div className="min-w-0 space-y-3 text-xs" data-testid="brand-report">
      <div className="flex flex-wrap items-center gap-2">
        <p className="flex-1 text-xs text-gray-500 dark:text-gray-400">
          What people search about {name}, and who holds page one for it. {progressLine(data.progress)}
          {data.started_at ? ` · started ${new Date(data.started_at).toLocaleDateString()}` : ''}. Clicks are estimates
          from position; this shows who is visible and who is aiming at the name - not who wins the customer.
        </p>
        {onSide && <SideToggle name={name} side={data.side ?? 'theirs'} onSide={onSide} />}
        {restart}
      </div>

      {data.site_warning && (
        <SiteWarning
          text={data.site_warning.text}
          onFix={() => onOpen(data.target.id)}
          evidence={data.site_warning.evidence}
          onOpen={onOpen}
        />
      )}

      <BrandTiles
        s={{
          started: true,
          target_share: m.target_share,
          interception: m.interception,
          third_party_share: m.third_party_share,
          ownership: m.ownership,
          number_one: m.number_one,
          branded_queries: m.branded_queries,
          paid_queries: m.paid_queries,
          aimed_pages: data.aimed_pages?.length ?? 0,
        }}
      />

      {(onWriteUp || data.write_up) && (
        <section className={section} data-testid="brand-write-up">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className={`${h3} flex-1`}>
              Written report{data.write_up?.at ? ` · ${new Date(data.write_up.at).toLocaleDateString()}` : ''}
            </h3>
            {data.write_up && (
              <button
                type="button"
                onClick={() => void navigator.clipboard?.writeText(data.write_up!.text)}
                className="px-2 py-0.5 text-xs rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                title="Copy it, to paste into a doc or an email"
              >
                Copy
              </button>
            )}
            {onWriteUp && (
              <button
                type="button"
                onClick={onWriteUp}
                disabled={writing || !data.progress?.searched}
                title={data.progress?.searched ? 'One model call, given only the numbers above' : 'Wait for the searches to come back'}
                className="px-2 py-0.5 text-xs rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                data-testid="brand-write"
              >
                {writing ? 'Writing…' : data.write_up ? 'Write it again' : 'Write it up'}
              </button>
            )}
          </div>
          {data.write_up ? (
            <div className="prose prose-sm max-w-none text-gray-800 dark:prose-invert dark:text-gray-100">
              <ChatMarkdown text={data.write_up.text} />
            </div>
          ) : (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              A summary and recommendations for a marketing or executive team, written from the numbers on this page.
            </p>
          )}
        </section>
      )}

      {(data.findings?.length ?? 0) > 0 && (
        <section className={section} data-testid="brand-findings">
          <h3 className={h3}>
            What the evidence shows {data.side === 'ours' ? `for ${name}` : `about ${name}`}
          </h3>
          <EnginesNote counts={engineCounts((data.rows ?? []).filter(r => r.status === 'searched'))} className="mb-1" />
          {/* RARE IS NOT HARMLESS. Most searches about a small company come
              back "under 10", and a list of them reads as nothing to worry
              about. Two things say otherwise: who searches a name with
              "alternatives", "pricing" or "vs" is close to deciding, and a
              rival that ranks there chose to aim at the name. */}
          {data.findings!.some(f => f.receipts.length > 0 && f.receipts.every(r => r.volume === 0)) && (
            <p className="mb-1 rounded bg-amber-50 px-2 py-1 text-xs text-amber-900 dark:bg-amber-900/20 dark:text-amber-100" data-testid="brand-rare-note">
              Some of these searches are rare (Google counts them as under 10 a month), but they still matter: people
              searching a name with “alternatives”, “pricing”, “vs” or “cancel” are close to deciding, and a rival
              ranking there chose to aim at {name}’s buyers.
              Biggest searches are listed first.
            </p>
          )}
          <ul>
            {data.findings!.map((f, i) => (
              <Finding key={`${f.level}-${f.who.name}-${i}`} f={f} onOpen={onOpen} onSerp={setSerp} />
            ))}
          </ul>
          <p className="mt-1 text-[11px] text-gray-400">
            {(data.history?.length ?? 0) === 0
              ? 'One look so far: it can show who is visible, not who has moved. Search again after a week to see movement.'
              : `Compared with the look on ${new Date(data.history![data.history!.length - 1].at).toLocaleDateString()}.`}
          </p>
          <WatchBar watch={data.watch} onWatch={onWatch} busy={watchBusy} />
        </section>
      )}

      {data.win && <WinSection win={data.win} name={name} side={data.side} onOpen={onOpen} onSerp={setSerp} />}

      {(data.interceptors?.length ?? 0) > 0 && (
        <section className={section} data-testid="brand-rivals-table">
          <DataTable
            table="brand_rivals"
            room={roomName}
            file={`brand-rivals-${data.target.name}`}
            title={<h3 className={h3}>Rivals on {name}’s searches</h3>}
            columns={rivalCols}
            rows={data.interceptors ?? []}
            rowKey={rivalKey}
            {...holderDetail(rivals, rivalKey, h => h.name ?? '')}
          />
        </section>
      )}

      {(data.aimed_pages?.length ?? 0) > 0 && (
        <section className={section} data-testid="brand-aimed">
          <DataTable
            table="brand_aimed"
            room={roomName}
            file={`brand-aimed-${data.target.name}`}
            title={<h3 className={h3}>Pages aimed at {name}</h3>}
            beforeTable={
              <p className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                Alternatives, comparison and review pages about {name}. A rival’s own page is deliberate targeting; a
                review site’s is the market talking.
              </p>
            }
            columns={aimedCols}
            rows={data.aimed_pages ?? []}
            rowKey={a => `aimed-${a.url}`}
          />
        </section>
      )}

      {(data.third_parties?.length ?? 0) > 0 && (
        <section className={section} data-testid="brand-thirds">
          <DataTable
            table="brand_thirds"
            room={roomName}
            file={`brand-third-parties-${data.target.name}`}
            title={<h3 className={h3}>Third parties on {name}’s searches</h3>}
            columns={thirdCols}
            rows={data.third_parties ?? []}
            rowKey={thirdKey}
            {...holderDetail(thirds, thirdKey, h => h.domain ?? '')}
          />
        </section>
      )}

      <section className={section} data-testid="brand-searches">
        <DataTable
          table="brand_searches"
          room={roomName}
          file={`brand-searches-${data.target.name}`}
          title={<h3 className={h3}>The searches</h3>}
          columns={rowCols}
          rows={data.rows ?? []}
          rowKey={r => `q-${r.q}`}
        />
      </section>

      {data.method && (
        <details className="text-xs text-gray-500 dark:text-gray-400" data-testid="brand-method">
          <summary className="cursor-pointer">How this was measured</summary>
          <ul className="mt-1 list-disc pl-4 space-y-0.5">
            <li>
              {data.progress.queries} searches built from {name}’s name, products, rivals and recorded founders;
              searched on {data.method.engines.map(engineLabel).join(', ') || 'the room’s search network'}
              {data.method.from ? `, ${new Date(data.method.from).toLocaleDateString()}` : ''}
              {data.method.to && data.method.to !== data.method.from ? ` to ${new Date(data.method.to).toLocaleDateString()}` : ''}.
            </li>
            <li>Volumes and bids: Google Ads, monthly averages. Clicks: {data.method.clicks}.</li>
            <li>Owners: a result is {name}’s or a rival’s when its address is that company’s site; anything else is a third party.</li>
            <li>Not measured: {data.method.not_measured.join('; ')}.</li>
          </ul>
        </details>
      )}

      {serp && <SerpModal isOpen onClose={() => setSerp(null)} keywordId={serp.id} keywordValue={serp.keyword} />}
    </div>
  );
}
